import { FolderOpen, Building2, FileText, CheckCircle2, Circle, Gavel } from "lucide-react";
import UploadZone from "@/components/dataroom/UploadZone";
import { CATEGORIES, categoriesFor, categorize } from "@/components/dataroom/DataroomSection";
import { buildChecklist, isRestructuration } from "@/lib/checklist";
import { labelDocument } from "@/lib/dataroom";

const GROUPS = [
    { key: "entite", holding: false, title: "Entité Auditée", icon: FolderOpen, hint: "Bilans, liasses fiscales, FEC et pièces de la société auditée." },
    { key: "holding", holding: true, title: "Holding / Groupe", icon: Building2, hint: "Comptes consolidés, liasses et FEC de la holding ou des sociétés du groupe (si applicable)." },
];

function Checklist({ items, testId }) {
    return (
        <ul data-testid={testId} className="grid gap-1.5 sm:grid-cols-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
            {items.map((i) => (
                <li key={i.type} data-testid={`${testId}-${i.type}`} className="flex items-start gap-2 text-sm">
                    {i.present ? <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" /> : <Circle className="h-4 w-4 mt-0.5 text-slate-300 shrink-0" />}
                    <span className={i.present ? "text-slate-900" : "text-slate-600"}>{labelDocument(i.type)}<span className={`ml-2 text-[10px] uppercase tracking-wider ${i.required ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{i.required ? "obligatoire" : "facultatif"}</span>{i.count > 0 && <span className="ml-1 text-xs text-slate-500">· {i.count}</span>}</span>
                </li>
            ))}
        </ul>
    );
}

/** Miroir exact de la Dataroom admin : Entité / Holding × (Comptables, Juridiques, Autres), checklist au-dessus des zones de dépôt. */
export default function PortailDataroom({ documents = [], contexteAudit, documentsRequis, onUpload }) {
    const isResto = isRestructuration(contexteAudit);
    return (
        <div className="space-y-10">
            {GROUPS.map((g) => {
                const list = documents.filter((d) => !!d.est_holding === g.holding);
                const Icon = g.icon;
                return (
                    <div key={g.key} data-testid={`portail-dataroom-${g.key}`} className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center"><Icon className="h-4 w-4 text-primary" strokeWidth={1.75} /></div>
                                <div><h3 className="font-display text-xl text-foreground">Dataroom — {g.title}</h3><p className="text-xs text-muted-foreground">{g.hint}</p></div>
                            </div>
                            <span data-testid={`portail-dataroom-count-${g.key}`} className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{list.length} document{list.length > 1 ? "s" : ""}</span>
                        </div>
                        <Checklist items={buildChecklist(contexteAudit, undefined, list, documentsRequis)} testId={`portail-checklist-${g.key}`} />
                        <UploadZone onFiles={(files, type, periode, tp) => onUpload(files, type, periode, tp, g.holding)} testPrefix={`portail-${g.key}`} />
                        <div className="space-y-4">
                            {categoriesFor(isResto).map((cat) => {
                                const sub = list.filter((d) => categorize(d) === cat.key);
                                const CIcon = cat.icon;
                                return (
                                    <div key={cat.key} data-testid={`portail-dataroom-${g.key}-${cat.key}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                                        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2.5 rounded-t-xl">
                                            <CIcon className="h-4 w-4 text-indigo-600" strokeWidth={1.75} />
                                            <h4 className="text-sm font-semibold text-slate-700">{cat.title}</h4>
                                            <span className="ml-auto text-xs text-slate-500">{sub.length} fichier{sub.length > 1 ? "s" : ""}</span>
                                        </div>
                                        {sub.length === 0 ? <p className="px-5 py-3 text-xs text-slate-400">{cat.empty}</p> : (
                                            <ul className="divide-y divide-slate-100">
                                                {sub.map((d) => (
                                                    <li key={d.id} data-testid={`portail-doc-${d.id}`} className="px-5 py-3 flex items-center gap-4 text-sm">
                                                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                                                        <div className="min-w-0 flex-1">
                                                            <span className="block truncate">{d.nom_fichier}</span>
                                                            {d.type_document === "pv_jugement" && (
                                                                d.remarques
                                                                    ? <span data-testid={`portail-doc-date-${d.id}`} className="mt-0.5 flex items-center gap-1 text-xs text-indigo-700"><Gavel className="h-3 w-3" /> {d.remarques}</span>
                                                                    : <span data-testid={`portail-doc-date-pending-${d.id}`} className="mt-0.5 block text-xs text-muted-foreground italic">Lecture automatique de la date…</span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-muted-foreground">{d.type_personnalise || labelDocument(d.type_document)}{d.periode ? ` · ${d.periode}` : ""}</span>
                                                        {d.uploaded_by_email && <span className="text-xs text-primary">{d.uploaded_by_email}</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
